<p-dialog
    header="Refund"
    [visible]="(viewModel$ | async)?.open"
    [modal]="true"
    [draggable]="false"
    [dismissableMask]="true"
    [breakpoints]="{ '40rem': '95vw' }"
    [style]="{ width: '32rem', maxWidth: '95vw' }"
    (visibleChange)="!$event && store.dispatch(actions.dialogClosed())"
>
    <ng-container *ngIf="viewModel$ | async as viewModel">

        <!-- The result. States the truth about the rail rather than guessing:
             `void` erases the charge, `refund` leaves it and adds a credit. Same
             money, different statement, and customers ask. -->
        <div *ngIf="viewModel.done">
            <!-- Requirement: a BlueSage refund is SENT when the request returns and
                 DONE when its leg settles — a terminal can take minutes to answer.
                 So this says which of the three it is, from the leg, and never
                 claims money went back before BlueSage has said so. -->
            <ng-container *ngIf="viewModel.outcome as outcome">
                <p *ngIf="outcome.state === 'waiting'">
                    <i class="pi pi-spin pi-spinner p-mr-2"></i>
                    Refund sent. Waiting for BlueSage to confirm
                    {{ viewModel.amount | currency }} to {{ viewModel.card }} — this can
                    take a few minutes if the terminal has to respond.
                    You can close this; the row on the job will update.
                </p>
                <p *ngIf="outcome.state === 'confirmed'">
                    <i class="pi pi-check-circle p-mr-2 bluesage-ok"></i>
                    Refunded {{ viewModel.amount | currency }} to {{ viewModel.card }}.
                </p>
                <!-- BlueSage said no and nothing moved: the operator can go straight
                     back to the form rather than close, reopen and retype. -->
                <p-messages severity="warn" *ngIf="outcome.state === 'refused'" [closable]="false">
                    <ng-template pTemplate>
                        <div>
                            <div>{{ outcome.message }}</div>
                            <p-button
                                class="p-mt-2"
                                size="small"
                                label="Try again"
                                (onClick)="store.dispatch(actions.tryAgainButtonClicked({ message: outcome.message }))"
                            ></p-button>
                        </div>
                    </ng-template>
                </p-messages>
                <!-- The outcome is UNKNOWN. No "try again" here on purpose: trying
                     again is how a customer gets refunded twice. -->
                <p-messages severity="error" *ngIf="outcome.state === 'unconfirmed'" [closable]="false">
                    <ng-template pTemplate>
                        <span>{{ outcome.message }}</span>
                    </ng-template>
                </p-messages>
                <p-messages severity="warn" *ngIf="outcome.state === 'overdue'" [closable]="false">
                    <ng-template pTemplate>
                        <span>
                            BlueSage has not confirmed this refund after several minutes.
                            Check the row on the job, and the payment in Valor, before
                            trying again.
                        </span>
                    </ng-template>
                </p-messages>
            </ng-container>
            <!-- Nothing reached a card. Saying otherwise would tell the operator
                 money went back that never left, and the customer would still be
                 waiting for it. -->
            <p *ngIf="!viewModel.movesMoney">
                <i class="pi pi-check-circle p-mr-2 bluesage-ok"></i>
                Recorded a {{ viewModel.amount | currency }} refund on this job.
                <strong>No money was moved.</strong> This payment was not taken
                through BlueSage, so refund the customer wherever it was taken and
                this row is the record of it.
            </p>
            <!-- ⚠ K27: a refund leg never reduces `depositTotal`, so a refunded
                 deposit still counts toward the job's deposit total. Claiming the
                 job totals are now correct would be a lie. -->
            <p class="p-text-secondary" *ngIf="!viewModel.outcome || viewModel.outcome.state === 'confirmed'">
                The job's deposit total does not change when a deposit is refunded —
                check the job totals before quoting them.
            </p>
        </div>

        <ng-container *ngIf="!viewModel.done">

            <p class="p-text-secondary" *ngIf="viewModel.nothingLeft">
                There is nothing left to refund on this transaction.
            </p>

            <ng-container *ngIf="!viewModel.nothingLeft">

                <!-- Requirement: a transaction taken on another system has no
                     BlueSage reference to refund against. `refundTransaction`
                     records the leg and moves nothing, which is correct
                     bookkeeping — but the operator has to know that the customer
                     is not getting their money from this button. -->
                <p-messages severity="warn" *ngIf="!viewModel.movesMoney" [closable]="false">
                    <ng-template pTemplate>
                        <span>
                            This payment was not taken through BlueSage, so this
                            <strong>records</strong> the refund on the job — it does not
                            send any money. Refund the customer wherever the payment was
                            originally taken.
                        </span>
                    </ng-template>
                </p-messages>

                <!-- Requirement: a charge run ON the terminal cannot take a partial
                     at any amount (V0573, a processor boundary). Say so and default
                     to full — but never hard-block the attempt, so the day Valor
                     enables it the path simply starts working. -->
                <p-messages severity="info" *ngIf="viewModel.standaloneOnly" [closable]="false">
                    <ng-template pTemplate>
                        <span>
                            This charge was taken on the terminal itself, so the card
                            network normally only allows a <strong>full</strong> refund.
                            If you need to refund part of it, refund it in full and
                            re-charge the difference, or push a refund to the terminal
                            and have the customer tap.
                        </span>
                    </ng-template>
                </p-messages>

                <label class="p-d-block p-mb-1" for="bluesage-refund-amount">Amount</label>
                <p-inputNumber
                    inputId="bluesage-refund-amount"
                    mode="currency"
                    currency="USD"
                    [min]="0"
                    [max]="viewModel.maxAmount"
                    [ngModel]="viewModel.amount"
                    (ngModelChange)="store.dispatch(actions.amountChanged({ amountMinor: toMinor($event) }))"
                ></p-inputNumber>
                <!-- The JOB-side cap only. The card figure lives on the quote
                     directly below, which follows the tip choice — stating a card
                     figure here as well meant two lines about the same money, and
                     the one up here could not see the checkbox. -->
                <small class="p-d-block p-text-secondary p-mt-1">
                    Up to {{ viewModel.maxAmount | currency }} is still refundable.
                </small>

                <!-- ⚠ The amount above is what comes off the JOB. What reaches the
                     CARD is that plus the differential this sale was charged for
                     paying by card — the mirror of the card price in Take Payment,
                     and the number the operator has to say out loud. Quoting only;
                     the figure sent is derived server-side.

                     The tip is NOT returned on a partial: it is the crew's money.
                     A FULL refund still returns it, because it reverses the whole
                     capture — so this line is absent there rather than stating a
                     figure that would be wrong. -->
                <p class="bluesage-hint" *ngIf="viewModel.cardRefund as back">
                    The customer gets
                    <strong>{{ back.toCard | currency }}</strong> back on the card
                    <span class="p-text-secondary" *ngIf="back.full">
                        — the whole payment reverses, including the card differential
                        and any tip.
                    </span>
                    <span class="p-text-secondary" *ngIf="!back.full">
                        — {{ viewModel.amount | currency }} off the job plus
                        {{ back.differential | currency }} card differential. The tip
                        is not refunded.
                    </span>
                </p>

                <!-- ⚠ A tip is the crew's money and the customer's own choice, so a
                     refund does not return it unless the operator says so. Offered
                     only on a FULL refund of a sale that carried one.

                     The consequence is stated, not buried: withholding the tip
                     means sending less than the whole capture, and the rail only
                     VOIDS when the figure clears it. So this is the difference
                     between an erased charge and a credit line, which is what the
                     customer sees on their statement. -->
                <div class="bluesage-refund-tip" *ngIf="viewModel.tipChoice as choice">
                    <p-checkbox
                        inputId="bluesage-refund-tip"
                        [binary]="true"
                        [ngModel]="choice.refundTip"
                        (ngModelChange)="store.dispatch(
                            actions.refundTipToggled({ refundTip: $event })
                        )"
                    ></p-checkbox>
                    <label for="bluesage-refund-tip">
                        Also refund the {{ choice.tip | currency }} tip
                        <small class="p-d-block p-text-secondary">
                            <ng-container *ngIf="!choice.refundTip">
                                Left with the crew. The charge stays on the customer's
                                statement with a credit against it, rather than being
                                erased.
                            </ng-container>
                            <ng-container *ngIf="choice.refundTip">
                                The whole payment reverses and the charge disappears
                                from the customer's statement.
                            </ng-container>
                        </small>
                    </label>
                </div>

                <!-- Required, and picked from a fixed list. The reason is the only
                     record of WHY a customer was given money back; a free-text box
                     nobody has to fill in produces a ledger of blanks. Short list on
                     purpose — categories people can pick without thinking are the
                     ones that get used honestly. -->
                <label class="p-d-block p-mt-3 p-mb-1">Reason</label>
                <div class="bluesage-refund-reasons">
                    <!-- `outlined` carries the selection VISUALLY only; a screen
                         reader hears four identical buttons without this.

                         ⚠ The state rides on `ariaLabel`, not on an
                         `[attr.aria-pressed]`. `p-button` renders its own inner
                         `<button>`, and an attribute bound on the `<p-button>`
                         host lands on that host — an element with no role, where
                         assistive technology ignores it. PrimeNG 17.18 exposes no
                         pass-through (`pt`) input on Button to reach the inner
                         element, so `ariaLabel` — which the component's template
                         binds straight to `[attr.aria-label]` on the real button
                         — is the supported way to say which one is chosen. -->
                    <p-button
                        *ngFor="let reason of reasons"
                        [label]="reason"
                        size="small"
                        [outlined]="viewModel.reasonCategory !== reason"
                        [ariaLabel]="viewModel.reasonCategory === reason
                            ? reason + ' — selected'
                            : reason"
                        (onClick)="store.dispatch(actions.reasonCategoryChanged({ reasonCategory: reason }))"
                    ></p-button>
                </div>

                <!-- ONLY for "Other", which records nothing on its own. Damage and
                     Billing error already say the useful part, and an optional box
                     beside them is a field everyone tabs past — YMM asked for it to
                     go on 2026-08-28. -->
                <ng-container *ngIf="viewModel.detailRequired">
                    <label class="p-d-block p-mt-2 p-mb-1" for="bluesage-refund-detail">
                        What happened <span class="p-text-secondary">(required)</span>
                    </label>
                    <input
                        id="bluesage-refund-detail"
                        pInputText
                        type="text"
                        class="p-inputtext-sm"
                        [ngModel]="viewModel.reasonDetail"
                        (ngModelChange)="store.dispatch(actions.reasonDetailChanged({ reasonDetail: $event }))"
                    />
                </ng-container>
                <small class="p-d-block p-text-secondary p-mt-1" *ngIf="!viewModel.reasonCategory">
                    Pick a reason before refunding — it is the only record of why.
                </small>

                <!-- Requirement: every wait state says what is being waited on. This
                     one is short: the request returns as soon as the refund's leg
                     exists, and the wait for the TERMINAL is the "Refund sent"
                     state above, which follows the leg. -->
                <p class="p-text-secondary p-mt-2" *ngIf="viewModel.submitting">
                    <i class="pi pi-spin pi-spinner p-mr-2"></i>
                    {{ viewModel.movesMoney ? 'Sending the refund to BlueSage…' : 'Recording the refund…' }}
                </p>

                <!-- The refusal carries the exact full-refund figure, which is what
                     makes the recovery ONE click rather than a retype. -->
                <p-messages severity="warn" *ngIf="viewModel.error" [closable]="false">
                    <ng-template pTemplate>
                        <div>
                            <div>{{ viewModel.error }}</div>
                            <p-button
                                class="p-mt-2"
                                size="small"
                                *ngIf="viewModel.fullRefundInstead !== undefined"
                                [label]="'Refund the full ' + (viewModel.fullRefundInstead | currency) + ' instead'"
                                (onClick)="store.dispatch(actions.refundFullInsteadButtonClicked())"
                            ></p-button>
                        </div>
                    </ng-template>
                </p-messages>
            </ng-container>
        </ng-container>
    </ng-container>

    <ng-template pTemplate="footer">
        <ng-container *ngIf="viewModel$ | async as viewModel">
          <div class="bluesage-dialog-footer">
            <p-button
                label="Close"
                severity="secondary"
                [text]="true"
                (onClick)="store.dispatch(actions.dialogClosed())"
            ></p-button>

            <!-- Requirement: the confirm carries the figure and the card, so nobody
                 has to remember what they typed two fields ago. -->
            <p-button
                *ngIf="!viewModel.done && !viewModel.nothingLeft"
                [label]="viewModel.movesMoney
                    ? 'Refund ' + (viewModel.amount | currency) + ' to ' + viewModel.card
                    : 'Record ' + (viewModel.amount | currency) + ' refund'"
                [loading]="viewModel.submitting"
                [disabled]="!viewModel.canConfirm"
                (onClick)="store.dispatch(actions.confirmButtonClicked())"
            ></p-button>
          </div>
        </ng-container>
    </ng-template>
</p-dialog>
